function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
let hasOwn = {}.hasOwnProperty;
export const classes = (...args) => {
  var clx = [];
  for (var i = 0; i < args.length; i++) {
    var arg = args[i];
    if (!arg) continue;
    var argType = typeof arg;
    if (argType === 'string' || argType === 'number') {
      clx.push(arg);
    } else if (Array.isArray(arg)) {
      if (arg.length) {
        var inner = classes(...arg);
        if (inner) {
          clx.push(inner);
        }
      }
    } else if (argType === 'object') {
      if (arg.toString === Object.prototype.toString) {
        for (var key in arg) {
          if (hasOwn.call(arg, key) && arg[key]) {
            clx.push(key);
          }
        }
      } else {
        clx.push(arg.toString());
      }
    }
  }
  return clx.join(' ');
};
export const makeTemplateComponent = (compClass, displayName) => {
  if (!displayName) {
    throw new Error('No displayName found for template component:', compClass);
  }
  const cmp = /*#__PURE__*/React.forwardRef(({
    children,
    className,
    ...rest
  }, ref) => /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classes(compClass, className)
  }, rest), children));
  cmp.displayName = displayName;
  return cmp;
};
const isPrimitive = val => val !== Object(val);
const depsEqual = (obj, prevObj) => {
  return _.isEqual(obj, prevObj);
};
export const useDeepCompareCallback = (effect, deps) => {
  if (process.env.NODE_ENV !== 'production') {
    if (!(deps instanceof Array) || !deps.length) {
      console.warn('`useDeepCompareCallback` should not be used with no dependencies. Use React.useCallback instead.');
    }
    if (deps.every(isPrimitive)) {
      console.warn('`useDeepCompareCallback` should not be used with dependencies that are all primitive values. Use React.useCallback instead.');
    }
  }
  const ref = useRef(undefined);
  if (!ref.current || !depsEqual(deps, ref.current)) {
    ref.current = deps;
  }
  return useCallback(effect, ref.current);
};
export const useDeepCompareMemo = (effect, deps) => {
  if (process.env.NODE_ENV !== 'production') {
    if (!(deps instanceof Array) || !deps.length) {
      console.warn('`useDeepCompareMemo` should not be used with no dependencies. Use React.useMemo instead.');
    }
    if (deps.every(isPrimitive)) {
      console.warn('`useDeepCompareMemo` should not be used with dependencies that are all primitive values. Use React.useMemo instead.');
    }
  }
  const ref = useRef(undefined);
  if (!ref.current || !depsEqual(deps, ref.current)) {
    ref.current = deps;
  }
  return useMemo(effect, ref.current);
};
export const useDeepCompareEffect = (effect, deps) => {
  if (process.env.NODE_ENV !== 'production') {
    if (!(deps instanceof Array) || !deps.length) {
      console.warn('`useDeepCompareEffect` should not be used with no dependencies. Use React.useEffect instead.');
    }
    if (deps.every(isPrimitive)) {
      console.warn('`useDeepCompareEffect` should not be used with dependencies that are all primitive values. Use React.useEffect instead.');
    }
  }
  const ref = useRef(undefined);
  if (!ref.current || !depsEqual(deps, ref.current)) {
    ref.current = deps;
  }
  useEffect(effect, ref.current);
};
export function useThrottleEffect(cb, additionalDeps, delay) {
  const options = {
    leading: true,
    trailing: true
  }; // pass custom lodash options
  const cbRef = useRef(cb);
  const throttledCb = React.useCallback(_.throttle((...args) => cbRef.current(...args), delay, options), [delay]);
  useEffect(() => {
    cbRef.current = cb;
  });
  // set additionalDeps to execute effect, when other values change (not only on delay change)
  useDeepCompareEffect(throttledCb, [throttledCb, ...additionalDeps]);
}